import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';
import IngestToNoteModal from './IngestToNoteModal';

export interface QmsClauseRef {
  framework: string;
  clause: string;
  title?: string;
}

export interface KnowledgeNote {
  path: string;
  title: string;
  frontmatter: Record<string, unknown>;
  body: string;
  raw: string;
  last_modified: string;
}

interface BacklinkRow {
  source_path: string;
  source_title: string;
  snippet: string | null;
}

interface BacklinksResponse {
  ok: boolean;
  target_path: string;
  backlinks: BacklinkRow[];
}

interface AlertsResponse {
  ok: boolean;
  path: string;
  days: number;
  rasff_count: number;
  hazard_name: string | null;
  hazard_names: string[];
  /** True when the note maps to a "NOT YET CATEGORISED (…)" placeholder and
   *  the backend has expanded the RASFF query to the whole
   *  hazardscategoryname (e.g. Allergens → "allergen risks" → 17 hazards). */
  category_expanded?: boolean;
  category_name?: string | null;
}

interface Props {
  note: KnowledgeNote;
  /** Called when the user clicks a [[Wikilink]] inside the body. */
  onWikilinkClick?: (target: string) => void;
  /**
   * Map of note title → vault-relative path. Used to give body wikilinks
   * a real `href` so middle-click / Cmd-click / right-click → Open in
   * new tab all navigate to the target note. When a title doesn't
   * resolve the anchor falls back to `href="#"` — the onClick handler
   * still tries to resolve by title via `onWikilinkClick`.
   */
  titleIndex?: Map<string, string>;
}

interface SaveResponse {
  ok: boolean;
  note: KnowledgeNote;
  reembedded: boolean;
  reembed_error: string | null;
}

async function saveNote(path: string, payload: {
  frontmatter?: Record<string, unknown>;
  body?: string;
  raw?: string;
}): Promise<SaveResponse> {
  const res = await authFetch(
    `${API_BASE_URL}/api/knowledge/notes/${encodeURIComponent(path)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Displays a single published knowledge note with body + sidebar metadata.
 * Supports in-place editing (form or raw YAML) and targeted document
 * ingestion via the existing worker pipeline.
 */
export default function KnowledgeNoteView({ note, onWikilinkClick, titleIndex }: Props) {
  const queryClient = useQueryClient();
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [ingestOpen, setIngestOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editMode, setEditMode] = useState<'form' | 'raw'>('form');

  // Draft state — initialised on entering edit mode, committed on save.
  const [draftFm, setDraftFm] = useState<FmRow[]>([]);
  const [draftBody, setDraftBody] = useState('');
  const [draftRaw, setDraftRaw] = useState('');
  const [saveMsg, setSaveMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // When the note changes (user navigates to another file) reset edit state.
  useEffect(() => {
    setEditing(false);
    setSaveMsg(null);
  }, [note.path]);

  const saveMut = useMutation(
    (payload: { frontmatter?: Record<string, unknown>; body?: string; raw?: string }) =>
      saveNote(note.path, payload),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['knowledgeNote', note.path], data.note);
        queryClient.invalidateQueries(['vaultTree']);
        setEditing(false);
        setSaveMsg({
          kind: 'success',
          text: data.reembedded
            ? 'Saved and re-indexed for search.'
            : `Saved.${data.reembed_error ? ' (re-embed skipped: ' + data.reembed_error + ')' : ''}`,
        });
      },
      onError: (err: Error) => {
        setSaveMsg({ kind: 'error', text: err.message });
      },
    },
  );

  // Derived view data (also used as seed for the editor form).
  const fm = note.frontmatter;
  const tags = asStringArray(fm.tags);
  const clauses = asClauseArray(fm.qms_clauses);
  const hazardName = asString(fm.hazard);
  const source = asString(fm.source);
  const severity = parseSeverity(fm.severity);
  const domain = asString(fm.domain);
  const lastReviewed = asString(fm['last-reviewed']) || asString(fm.last_reviewed);
  const rasffCount = asNumber(fm.rasff_count_12m);
  const dimHazardsId = asNumber(fm.dimhazards_id);

  const renderedBody = useMemo(() => transformWikilinks(note.body), [note.body]);

  const crumbs = note.path.split('/');
  const folderCrumbs = crumbs.slice(0, -1);

  // Enter edit mode → seed draft state from the current note.
  function beginEdit() {
    setDraftFm(fmToRows(note.frontmatter));
    setDraftBody(note.body);
    setDraftRaw(note.raw);
    setEditMode('form');
    setEditing(true);
    setSaveMsg(null);
  }

  function cancelEdit() {
    setEditing(false);
    setSaveMsg(null);
  }

  function handleSave() {
    if (editMode === 'raw') {
      saveMut.mutate({ raw: draftRaw });
    } else {
      const fmObj = rowsToFm(draftFm);
      saveMut.mutate({ frontmatter: fmObj, body: draftBody });
    }
  }

  async function handleCopyPath() {
    try {
      await navigator.clipboard.writeText(note.path);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1600);
    } catch {
      /* clipboard unavailable */
    }
  }

  // Group clauses by framework for a cleaner sidebar layout.
  const clausesByFramework = useMemo(() => {
    const map = new Map<string, QmsClauseRef[]>();
    for (const c of clauses) {
      const arr = map.get(c.framework) ?? [];
      arr.push(c);
      map.set(c.framework, arr);
    }
    return Array.from(map.entries());
  }, [clauses]);

  // Backlinks — other notes that [[wikilink]] to this one. Loaded from the
  // server-side reverse index (curated.knowledge_backlinks).
  const backlinksQuery = useQuery<BacklinksResponse, Error>(
    ['knowledgeBacklinks', note.path],
    async () => {
      const res = await authFetch(
        `${API_BASE_URL}/api/knowledge/backlinks/${encodeURIComponent(note.path)}`,
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    { staleTime: 30_000 },
  );
  const backlinks = backlinksQuery.data?.backlinks ?? [];
  const hasBacklinks = backlinks.length > 0;

  // What's new — recent RASFF + food_alerts matching this note's hazard FKs.
  const [alertDays, setAlertDays] = useState<30 | 90 | 0>(30);
  const alertsQuery = useQuery<AlertsResponse, Error>(
    ['knowledgeAlerts', note.path, alertDays],
    async () => {
      const res = await authFetch(
        `${API_BASE_URL}/api/knowledge/alerts-for-note/${encodeURIComponent(note.path)}?days=${alertDays}`,
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    { staleTime: 30_000 },
  );
  const rasffCountForHazard = alertsQuery.data?.rasff_count ?? 0;
  const alertHazardName = alertsQuery.data?.hazard_name ?? null;
  const alertCategoryExpanded = alertsQuery.data?.category_expanded ?? false;
  const hasAlerts = !!alertHazardName; // panel renders whenever the note has a hazard FK

  // Sidebar renders whenever frontmatter metadata, backlinks, or alerts are present.
  const hasSidebar =
    !!hazardName || !!severity || !!domain || !!lastReviewed ||
    rasffCount !== undefined || tags.length > 0 || clauses.length > 0 || !!source ||
    hasBacklinks || hasAlerts;

  return (
    <article className="knowledge-note">
      <header className="knowledge-note__header">
        <nav className="knowledge-note__breadcrumb" aria-label="Vault path">
          {folderCrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              <span className="knowledge-note__breadcrumb-part">{crumb}</span>
              <span className="knowledge-note__breadcrumb-sep" aria-hidden>/</span>
            </React.Fragment>
          ))}
          <span className="knowledge-note__breadcrumb-file">{crumbs[crumbs.length - 1]}</span>
        </nav>
        <h1 className="knowledge-note__title">{note.title}</h1>
        <div className="knowledge-note__meta-row">
          <span className="knowledge-note__meta-item">
            <span className="knowledge-note__meta-icon" aria-hidden>{'\u{1F4C5}'}</span>
            Updated {formatDate(note.last_modified)}
          </span>
          {domain && (
            <span className="knowledge-note__meta-item">
              <span className="knowledge-note__meta-icon" aria-hidden>{'\u{1F4C2}'}</span>
              {domain}
            </span>
          )}
          <div className="knowledge-note__actions">
            {!editing && (
              <>
                <button
                  className="knowledge-note__action knowledge-note__action--primary"
                  onClick={() => setIngestOpen(true)}
                  type="button"
                  title="Ingest a document or URL into this note"
                >
                  <span aria-hidden>{'\u{1F4E5}'}</span> Ingest
                </button>
                <button
                  className="knowledge-note__action"
                  onClick={beginEdit}
                  type="button"
                >
                  <span aria-hidden>{'\u270E'}</span> Edit
                </button>
                <button
                  className="knowledge-note__action"
                  onClick={handleCopyPath}
                  type="button"
                  title="Copy vault-relative path"
                >
                  {copyState === 'copied' ? '\u2713 Copied' : 'Copy path'}
                </button>
              </>
            )}
            {editing && (
              <>
                <div className="knowledge-note__mode-switch" role="tablist">
                  <button
                    role="tab"
                    aria-selected={editMode === 'form'}
                    type="button"
                    className={`knowledge-note__mode${editMode === 'form' ? ' knowledge-note__mode--active' : ''}`}
                    onClick={() => setEditMode('form')}
                  >
                    Form
                  </button>
                  <button
                    role="tab"
                    aria-selected={editMode === 'raw'}
                    type="button"
                    className={`knowledge-note__mode${editMode === 'raw' ? ' knowledge-note__mode--active' : ''}`}
                    onClick={() => {
                      if (editMode === 'form') {
                        // Form → Raw: sync the current form draft into raw so users see it.
                        setDraftRaw(stringifyFmRows(draftFm) + '\n' + draftBody);
                      }
                      setEditMode('raw');
                    }}
                  >
                    Raw YAML
                  </button>
                </div>
                <button
                  className="knowledge-note__action"
                  onClick={cancelEdit}
                  type="button"
                  disabled={saveMut.isLoading}
                >
                  Cancel
                </button>
                <button
                  className="knowledge-note__action knowledge-note__action--primary"
                  onClick={handleSave}
                  type="button"
                  disabled={saveMut.isLoading}
                >
                  {saveMut.isLoading ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
        {saveMsg && (
          <div className={`knowledge-note__save-msg knowledge-note__save-msg--${saveMsg.kind}`}>
            {saveMsg.text}
          </div>
        )}
      </header>

      {editing ? (
        <div className="knowledge-note__editor">
          {editMode === 'form' ? (
            <FormEditor
              rows={draftFm}
              body={draftBody}
              onRowsChange={setDraftFm}
              onBodyChange={setDraftBody}
            />
          ) : (
            <RawEditor value={draftRaw} onChange={setDraftRaw} />
          )}
        </div>
      ) : (
        <div className={`knowledge-note__layout${hasSidebar ? '' : ' knowledge-note__layout--no-sidebar'}`}>
          <div className="knowledge-note__body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              // react-markdown ≥ 9 ships a default `urlTransform` that
              // drops anything whose scheme isn't http/https/mailto/tel/
              // irc/ircs/xmpp — our synthetic `wikilink:` scheme (emitted
              // by `transformWikilinks`) was being stripped to an empty
              // href, so the wikilink branch below never fired. Pass
              // everything through; we handle `wikilink:` ourselves and
              // the fallback branch opens real URLs in a new tab.
              urlTransform={(url) => url}
              components={{
                a: ({ href, children, ...rest }) => {
                  if (href && href.startsWith('wikilink:')) {
                    const target = decodeURIComponent(href.slice('wikilink:'.length));
                    // Try to resolve title → vault path at render time so
                    // the anchor has a real URL. This makes middle-click /
                    // Cmd-click / right-click → Open in new tab navigate
                    // to the linked note instead of the current page.
                    let resolved = titleIndex?.get(target);
                    if (!resolved && titleIndex) {
                      const lower = target.toLowerCase();
                      for (const [k, v] of titleIndex.entries()) {
                        if (k.toLowerCase() === lower) {
                          resolved = v;
                          break;
                        }
                      }
                    }
                    const resolvedHref = resolved
                      ? `/knowledge?path=${encodeURIComponent(resolved)}`
                      : '#';
                    return (
                      <a
                        {...rest}
                        className={`wikilink${resolved ? '' : ' wikilink--unresolved'}`}
                        href={resolvedHref}
                        onClick={(e) => {
                          // Let the browser open new-tab clicks natively
                          // (only possible when href is real).
                          if (
                            resolved &&
                            (e.button !== 0 ||
                              e.metaKey ||
                              e.ctrlKey ||
                              e.shiftKey ||
                              e.altKey)
                          ) {
                            return;
                          }
                          e.preventDefault();
                          onWikilinkClick?.(target);
                        }}
                      >
                        {children}
                      </a>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noreferrer" {...rest}>
                      {children}
                    </a>
                  );
                },
              }}
            >
              {renderedBody}
            </ReactMarkdown>
          </div>

          {hasSidebar && (
            <aside className="knowledge-note__sidebar">
              {hasBacklinks && (
                <BacklinksPanel
                  backlinks={backlinks}
                  onOpen={(p) => onWikilinkClick?.(p)}
                />
              )}
              {hasAlerts && (
                <WhatsNewPanel
                  hazardName={alertHazardName as string}
                  rasffCount={rasffCountForHazard}
                  days={alertDays}
                  onDaysChange={setAlertDays}
                  loading={alertsQuery.isLoading}
                  categoryExpanded={alertCategoryExpanded}
                />
              )}
              {severity && (
                <MetaSection label="Severity">
                  <SeverityBar level={severity.level} description={severity.description} />
                </MetaSection>
              )}
              {hazardName && (
                <MetaSection label="Hazard">
                  <div className="meta-value">{hazardName}</div>
                </MetaSection>
              )}
              {rasffCount !== undefined && (
                <MetaSection label="RASFF (12m)">
                  <div className="meta-value meta-value--big">{rasffCount.toLocaleString()}</div>
                </MetaSection>
              )}
              {tags.length > 0 && (
                <MetaSection label={`Tags (${tags.length})`}>
                  <div className="meta-chips">
                    {tags.map((t) => (
                      <span key={t} className="meta-chip">{t}</span>
                    ))}
                  </div>
                </MetaSection>
              )}
              {clausesByFramework.length > 0 && (
                <MetaSection label="QMS Clauses">
                  {clausesByFramework.map(([fw, list]) => (
                    <div key={fw} className="meta-clause-group">
                      <div className="meta-clause-group__fw">{fw.replace('_', ' ')}</div>
                      <ul className="meta-clauses">
                        {list.map((c, i) => (
                          <li key={`${c.clause}-${i}`} className="meta-clause">
                            <span className="meta-clause__clause">{c.clause}</span>
                            {c.title && <span className="meta-clause__title">{c.title}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </MetaSection>
              )}
              {lastReviewed && (
                <MetaSection label="Last Reviewed">
                  <div className="meta-value meta-value--small">{lastReviewed}</div>
                </MetaSection>
              )}
              {source && (
                <MetaSection label="Source">
                  <div className="meta-value meta-value--small">{source}</div>
                </MetaSection>
              )}
            </aside>
          )}
        </div>
      )}

      {ingestOpen && (
        <IngestToNoteModal
          targetPath={note.path}
          hazardId={dimHazardsId}
          hazardLabel={hazardName}
          onClose={() => setIngestOpen(false)}
        />
      )}
    </article>
  );
}

// ── MetaSection ────────────────────────────────────────────────────────────

function MetaSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="meta-section">
      <h3 className="meta-section__label">{label}</h3>
      {children}
    </section>
  );
}

// ── Backlinks panel ────────────────────────────────────────────────────────

function BacklinksPanel({
  backlinks,
  onOpen,
}: {
  backlinks: BacklinkRow[];
  onOpen: (path: string) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section className="meta-section meta-section--backlinks">
      <button
        type="button"
        className="meta-section__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="meta-section__label">
          Backlinks ({backlinks.length})
        </span>
        <span aria-hidden>{open ? '\u25BE' : '\u25B8'}</span>
      </button>
      {open && (
        <ul className="backlinks-list">
          {backlinks.map((b) => (
            <li key={b.source_path} className="backlinks-list__item">
              <a
                href={`/knowledge?path=${encodeURIComponent(b.source_path)}`}
                className="backlinks-list__link"
                onClick={(e) => {
                  // Let the browser handle any "open in new tab" intent
                  // natively — middle-click, Cmd/Ctrl-click, Shift-click,
                  // right-click → Open in new tab all follow the real
                  // href and load the linked note directly.
                  if (
                    e.button !== 0 ||
                    e.metaKey ||
                    e.ctrlKey ||
                    e.shiftKey ||
                    e.altKey
                  ) {
                    return;
                  }
                  // Plain left-click: SPA nav via state, no full reload.
                  e.preventDefault();
                  onOpen(b.source_path);
                }}
                title={b.source_path}
              >
                {b.source_title}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── What's new panel ──────────────────────────────────────────────────────

function WhatsNewPanel({
  hazardName,
  rasffCount,
  days,
  onDaysChange,
  loading,
  categoryExpanded,
}: {
  hazardName: string;
  rasffCount: number;
  days: 30 | 90 | 0;
  onDaysChange: (d: 30 | 90 | 0) => void;
  loading: boolean;
  /** When true, `hazardName` is actually a hazardscategoryname (e.g.
   *  "allergen risks") — deep-link with ?hazardCategory=… instead of
   *  ?hazard=…, because the RASFF dashboard supports both filter keys
   *  but the category key is what matches here. */
  categoryExpanded: boolean;
}) {
  const [open, setOpen] = useState(true);
  // Dashboard deep-link: pre-fill the right filter + time window so the
  // result set matches what we counted here.
  const filterKey = categoryExpanded ? 'hazardCategory' : 'hazard';
  const dashboardHref = `/rasff?${filterKey}=${encodeURIComponent(hazardName)}&days=${days || 'all'}`;
  const windowLabel =
    days === 0 ? 'all-time' : days === 90 ? 'last 90d' : 'last 30d';
  return (
    <section className="meta-section meta-section--whatsnew">
      <button
        type="button"
        className="meta-section__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="meta-section__label">
          Last RASFF hits{loading ? ' …' : ''}
        </span>
        <span aria-hidden>{open ? '\u25BE' : '\u25B8'}</span>
      </button>
      {open && (
        <div className="whatsnew-summary">
          <div className="meta-value meta-value--big">{rasffCount.toLocaleString()}</div>
          <div className="whatsnew-sub">{windowLabel}</div>
          <div className="whatsnew-row">
            <select
              className="whatsnew-select"
              aria-label="Time window"
              value={days}
              onChange={(e) => onDaysChange(Number(e.target.value) as 30 | 90 | 0)}
            >
              <option value={30}>30d</option>
              <option value={90}>90d</option>
              <option value={0}>All</option>
            </select>
            <a className="whatsnew-link" href={dashboardHref}>
              View on dashboard →
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Severity bar ──────────────────────────────────────────────────────────

function SeverityBar({ level, description }: { level: number; description?: string }) {
  const cells = [1, 2, 3, 4, 5];
  const severityClass = `severity-bar--l${Math.min(Math.max(level, 1), 5)}`;
  return (
    <div className={`severity-bar ${severityClass}`}>
      <div className="severity-bar__scale">
        {cells.map((c) => (
          <span
            key={c}
            className={`severity-bar__cell${c <= level ? ' severity-bar__cell--filled' : ''}`}
          />
        ))}
      </div>
      <div className="severity-bar__text">
        <span className="severity-bar__level">{level} / 5</span>
        {description && <span className="severity-bar__desc">{description}</span>}
      </div>
    </div>
  );
}

// ── Form editor ──────────────────────────────────────────────────────────

interface FmRow {
  id: number;
  key: string;
  value: string; // always stringified — parsed back on submit
  kind: 'text' | 'tags' | 'json';
}

const TAG_LIKE_KEYS = new Set(['tags']);
const JSON_LIKE_KEYS = new Set(['qms_clauses']);

function fmToRows(fm: Record<string, unknown>): FmRow[] {
  let nextId = 1;
  const rows: FmRow[] = [];
  for (const [key, value] of Object.entries(fm)) {
    let kind: FmRow['kind'] = 'text';
    let str = '';
    if (Array.isArray(value) && TAG_LIKE_KEYS.has(key)) {
      kind = 'tags';
      str = value.filter((x) => typeof x === 'string').join(', ');
    } else if (
      (Array.isArray(value) || (typeof value === 'object' && value !== null)) &&
      (JSON_LIKE_KEYS.has(key) || !TAG_LIKE_KEYS.has(key))
    ) {
      kind = 'json';
      str = JSON.stringify(value, null, 2);
    } else if (value === null || value === undefined) {
      str = '';
    } else {
      str = String(value);
    }
    rows.push({ id: nextId++, key, value: str, kind });
  }
  return rows;
}

function rowsToFm(rows: FmRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    if (!row.key.trim()) continue;
    if (row.kind === 'tags') {
      const parts = row.value.split(',').map((s) => s.trim()).filter(Boolean);
      out[row.key] = parts;
    } else if (row.kind === 'json') {
      const trimmed = row.value.trim();
      if (!trimmed) {
        out[row.key] = [];
      } else {
        try {
          out[row.key] = JSON.parse(trimmed);
        } catch {
          // Leave as raw string if it won't parse; the user can fix it.
          out[row.key] = trimmed;
        }
      }
    } else {
      const t = row.value.trim();
      if (t === '') out[row.key] = null;
      else if (/^-?\d+(\.\d+)?$/.test(t)) out[row.key] = Number(t);
      else if (t === 'true') out[row.key] = true;
      else if (t === 'false') out[row.key] = false;
      else out[row.key] = t;
    }
  }
  return out;
}

function stringifyFmRows(rows: FmRow[]): string {
  const lines = ['---'];
  for (const row of rows) {
    if (!row.key.trim()) continue;
    if (row.kind === 'json') {
      const trimmed = row.value.trim();
      lines.push(`${row.key}: ${trimmed || '[]'}`);
    } else if (row.kind === 'tags') {
      const parts = row.value.split(',').map((s) => s.trim()).filter(Boolean);
      lines.push(`${row.key}: ${JSON.stringify(parts)}`);
    } else {
      lines.push(`${row.key}: ${row.value}`);
    }
  }
  lines.push('---', '');
  return lines.join('\n');
}

function FormEditor({
  rows,
  body,
  onRowsChange,
  onBodyChange,
}: {
  rows: FmRow[];
  body: string;
  onRowsChange: (rows: FmRow[]) => void;
  onBodyChange: (body: string) => void;
}) {
  const [newKey, setNewKey] = useState('');

  function updateRow(id: number, updates: Partial<FmRow>) {
    onRowsChange(rows.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  }
  function removeRow(id: number) {
    onRowsChange(rows.filter((r) => r.id !== id));
  }
  function addRow() {
    const k = newKey.trim();
    if (!k) return;
    const nextId = rows.reduce((m, r) => Math.max(m, r.id), 0) + 1;
    const kind: FmRow['kind'] =
      TAG_LIKE_KEYS.has(k) ? 'tags' : JSON_LIKE_KEYS.has(k) ? 'json' : 'text';
    onRowsChange([...rows, { id: nextId, key: k, value: '', kind }]);
    setNewKey('');
  }

  return (
    <div className="note-editor">
      <div className="note-editor__section">
        <div className="note-editor__section-header">
          <h3 className="note-editor__section-title">Frontmatter</h3>
          <span className="note-editor__section-hint">
            Shared metadata. Arrays use JSON; <code>tags</code> is a comma list.
          </span>
        </div>
        <div className="note-editor__fm-rows">
          {rows.length === 0 && (
            <div className="note-editor__empty">No frontmatter yet — add a key below.</div>
          )}
          {rows.map((row) => (
            <div key={row.id} className={`fm-row fm-row--${row.kind}`}>
              <input
                className="fm-row__key"
                value={row.key}
                onChange={(e) => updateRow(row.id, { key: e.target.value })}
                placeholder="key"
                spellCheck={false}
              />
              {row.kind === 'json' ? (
                <textarea
                  className="fm-row__value fm-row__value--json"
                  value={row.value}
                  onChange={(e) => updateRow(row.id, { value: e.target.value })}
                  placeholder='[]'
                  spellCheck={false}
                  rows={row.value.split('\n').length + 1}
                />
              ) : (
                <input
                  className="fm-row__value"
                  value={row.value}
                  onChange={(e) => updateRow(row.id, { value: e.target.value })}
                  placeholder={row.kind === 'tags' ? 'tag1, tag2, ...' : 'value'}
                  spellCheck={false}
                />
              )}
              <button
                className="fm-row__remove"
                type="button"
                onClick={() => removeRow(row.id)}
                aria-label={`Remove ${row.key}`}
                title="Remove"
              >
                {'\u2715'}
              </button>
            </div>
          ))}
        </div>
        <div className="note-editor__add-row">
          <input
            className="note-editor__add-key"
            placeholder="Add a key (e.g. severity)"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRow(); } }}
          />
          <button type="button" className="btn btn--ghost" onClick={addRow} disabled={!newKey.trim()}>
            + Add
          </button>
        </div>
      </div>

      <div className="note-editor__section">
        <div className="note-editor__section-header">
          <h3 className="note-editor__section-title">Body</h3>
          <span className="note-editor__section-hint">
            Markdown — <code>##</code> for sections, <code>[[Wiki]]</code> for cross-links.
          </span>
        </div>
        <textarea
          className="note-editor__body"
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          spellCheck={false}
          rows={24}
        />
      </div>
    </div>
  );
}

function RawEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="note-editor">
      <div className="note-editor__section">
        <div className="note-editor__section-header">
          <h3 className="note-editor__section-title">Raw file</h3>
          <span className="note-editor__section-hint">
            The entire note — YAML frontmatter + body. Advanced use.
          </span>
        </div>
        <textarea
          className="note-editor__raw"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          rows={40}
        />
      </div>
    </div>
  );
}

// ── Body transform: [[Wikilinks]] → markdown links ────────────────────────

function transformWikilinks(body: string): string {
  return body.replace(/\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]/g, (_m, target: string, alias?: string) => {
    const label = (alias ?? target).trim();
    const enc = encodeURIComponent(target.trim());
    return `[${label}](wikilink:${enc})`;
  });
}

// ── Frontmatter coercion helpers ──────────────────────────────────────────

function asString(v: unknown): string | undefined {
  if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  return undefined;
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (typeof v === 'string' && v.length > 0) return [v];
  return [];
}

function asClauseArray(v: unknown): QmsClauseRef[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((item): QmsClauseRef[] => {
    if (typeof item !== 'object' || item === null) return [];
    const o = item as Record<string, unknown>;
    if (typeof o.framework === 'string' && typeof o.clause === 'string') {
      return [{
        framework: o.framework,
        clause: o.clause,
        title: typeof o.title === 'string' ? o.title : undefined,
      }];
    }
    return [];
  });
}

/**
 * Severity field may be "5", "5 (Very High — life threatening)", or just a
 * description. Extract a 1-5 numeric level + trailing description.
 */
function parseSeverity(v: unknown): { level: number; description?: string } | undefined {
  if (typeof v === 'number' && v >= 1 && v <= 5) return { level: Math.round(v) };
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s) return undefined;
  const m = s.match(/^\s*([1-5])\b[\s\-–—:]*(.*)$/);
  if (m) {
    const level = parseInt(m[1], 10);
    const rest = m[2].replace(/^[\s()\-–—]+|[\s()\-–—]+$/g, '').trim();
    return { level, description: rest || undefined };
  }
  return undefined;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
